import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Activity } from 'api/activities/activities.types';
import { useCreateExternalServiceCode } from 'api/externalServiceCode';
import { StyledButton } from 'components/Buttons';
import { StyledTextField } from 'components/Inputs';
import _ from 'lodash';
import { Counterparty } from 'pages/invoices/invoices.types';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { reactPaths } from 'root/pathnames';
import { appToast } from 'utils/toast.utils';
import {
  addExternalActivityError,
  addExternalActivitySuccess,
  buttons,
  mainColumnHeaders,
  maxCodeLength,
  requiredLabel,
  maxLengthLabel,
} from '../MappingModule.consts';
import { CounterpartySelect } from './elements/CounterpartySelect';
import { ServiceSelect } from './elements/ServiceSelect';
import * as S from './MappingForm.css';

interface FormInputs {
  service: Activity | null;
  counterparty: Counterparty | null;
  externalName: string;
}

export const MappingForm = () => {
  const navigate = useNavigate();

  const { createExternalServiceCode } = useCreateExternalServiceCode();

  const [submitButtonDisabled, setSubmitButtonDisabled] =
    useState<boolean>(false);

  const {
    handleSubmit,
    control,
    trigger,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onChange',
    defaultValues: {
      service: null,
      counterparty: null,
      externalName: '',
    },
  });

  useEffect(() => {
    trigger();
  }, [trigger]);

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const { service, counterparty, externalName } = data;
    if (!service) return;

    setSubmitButtonDisabled(true);
    try {
      const response = await createExternalServiceCode({
        activityId: service.id,
        counterpartyId: counterparty?.id ?? 0,
        externalName,
      });
      if (response?.data?.status === 'OK') {
        appToast.success(addExternalActivitySuccess);
        navigate(reactPaths.mappingModule);
      }
    } catch (error) {
      appToast.error(addExternalActivityError);
    } finally {
      setSubmitButtonDisabled(false);
    }
  };

  const handleCancel = () => {
    navigate(reactPaths.mappingModule);
  };

  return (
    <Box display='flex' flexDirection='column'>
      <S.Form onSubmit={handleSubmit(onSubmit)}>
        <S.Title>{buttons.addExternalActivity}</S.Title>

        <S.FieldGroup>
          <S.FieldLabel>{mainColumnHeaders.internalActivity}</S.FieldLabel>
          <Controller
            name='service'
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <ServiceSelect field={field} error={!!errors.service} />
            )}
          />
          <S.ErrorWrapper>
            {errors.service && <S.Error>{requiredLabel}</S.Error>}
          </S.ErrorWrapper>
        </S.FieldGroup>

        <S.FieldGroup>
          <S.FieldLabel>{mainColumnHeaders.counterparty}</S.FieldLabel>
          <Controller
            name='counterparty'
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <CounterpartySelect field={field} error={!!errors.counterparty} />
            )}
          />
          <S.ErrorWrapper>
            {errors.counterparty && <S.Error>{requiredLabel}</S.Error>}
          </S.ErrorWrapper>
        </S.FieldGroup>

        <S.FieldGroup>
          <S.FieldLabel>{mainColumnHeaders.externalActivity}</S.FieldLabel>
          <Controller
            name='externalName'
            control={control}
            rules={{ required: true, maxLength: maxCodeLength }}
            render={({ field }) => (
              <StyledTextField
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={!!errors.externalName}
                size='small'
                variant='outlined'
                padding='dense'
                width={280}
              />
            )}
          />
          <S.ErrorWrapper>
            {errors.externalName?.type === 'required' && (
              <S.Error>{requiredLabel}</S.Error>
            )}
            {errors.externalName?.type === 'maxLength' && (
              <S.Error>{maxLengthLabel}</S.Error>
            )}
          </S.ErrorWrapper>
        </S.FieldGroup>

        <S.ActionsContainer>
          <StyledButton
            $primary
            type='submit'
            disabled={(!_.isEmpty(errors) || submitButtonDisabled) as boolean}
          >
            {buttons.save}
          </StyledButton>
          <StyledButton type='button' onClick={handleCancel}>
            {buttons.cancel}
          </StyledButton>
        </S.ActionsContainer>
      </S.Form>
    </Box>
  );
};
